import React from 'react';
import { V } from '../../../utils/theme';
import { Card } from '../../../components/ui';
import { STATUS_COLOR } from './shared';
import { adherenceLabel, consentRows, flagKeysFrom, num, pctText } from './helpers';

// ─── Organization card: the white-label view of one account ───
// Renders `user_detail.org` from /api/admin — the block the API returns for an admin
// looking at a member, and `null` for a solo user. Shared by the admin panel's user
// detail (tabs/features/profile.jsx) and the User Audit panel's Gym tab so the two
// screens can never drift.
//
// Counts only. The API deliberately sends unread *numbers* and never a message body,
// and this card never asks for one.

const day = (v) => (v ? String(v).slice(0, 10) : '—');

const Row = ({ k, v, color, mono, testId }) => (
  <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "baseline", fontSize: 12, paddingTop: 3, paddingBottom: 3 }}>
    <span style={{ color: V.text3, flexShrink: 0 }}>{k}</span>
    <span data-testid={testId} style={{ color: color || V.text, textAlign: "right", minWidth: 0, wordBreak: "break-word", fontFamily: mono ? V.mono : undefined }}>{v}</span>
  </div>
);

const Block = ({ title, children, testId }) => (
  <div data-testid={testId} style={{ marginTop: 10 }}>
    <div style={{ fontSize: 10, color: V.text3, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 700, marginBottom: 4 }}>{title}</div>
    <div style={{ borderRadius: 10, background: V.inputBg, paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10 }}>{children}</div>
  </div>
);

const Tile = ({ l, v, c }) => (
  <div style={{ paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8, borderRadius: 8, background: V.inputBg }}>
    <div style={{ fontSize: 9, color: V.text3, textTransform: "uppercase", letterSpacing: ".06em" }}>{l}</div>
    <div style={{ fontSize: 14, fontWeight: 700, color: c || V.text, fontFamily: V.mono }}>{v}</div>
  </div>
);

export function OrgContextCard({ org, email, style = {} }) {
  // `null` is an answer, not a missing value: this account is not in any gym.
  if (!org) {
    return (
      <div data-testid="org-context-solo">
        <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12, ...style }}>
          <div style={{ fontSize: 12, color: V.text3 }}>
            <b style={{ color: V.text2 }}>Solo user</b> — not a member of any gym.
          </div>
        </Card>
      </div>
    );
  }

  const o = org.org || {};
  const m = org.membership || {};
  const adh = org.adherence;
  const goals = org.goals || [];
  const events = org.consent_events || [];
  const flags = org.flags?.effective || {};
  const keys = flagKeysFrom(org.flags?.keys, [flags]);
  const off = keys.filter(k => flags[k] === false);
  const shared = consentRows(org.consent).filter(r => r.shared).length;
  const statusColor = STATUS_COLOR[o.status] || V.text3;

  return (
    <div data-testid="org-context">
    <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12, ...style }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 16 }} aria-hidden>🏢</span>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div data-testid="org-context-name" style={{ fontSize: 14, fontWeight: 800, color: V.text }}>{o.name || 'Unknown gym'}</div>
          <div style={{ fontSize: 10, color: V.text3, fontFamily: V.mono }}>{o.slug || '—'} · {o.kind || '—'}</div>
        </div>
        <span style={{ fontSize: 9, fontWeight: 700, color: statusColor, background: `${statusColor}18`, borderRadius: 8, paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8, textTransform: "uppercase", letterSpacing: ".04em" }}>{o.status || 'unknown'}</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: 6, marginTop: 10 }}>
        <Tile l="Role" v={m.role || '—'} c={m.role === 'owner' ? V.warn : m.role === 'coach' ? V.accent2 : V.text} />
        <Tile l="Joined" v={day(m.joined_at)} />
        <Tile l="Sharing" v={`${shared}/5`} c={shared ? V.accent : V.text3} />
        <Tile l="Unread msgs" v={num(org.unread?.messages ?? 0)} />
        <Tile l="Unread anns" v={num(org.unread?.announcements ?? 0)} />
      </div>

      {m.status && m.status !== 'active' && (
        <div style={{ fontSize: 11, color: V.warn, marginTop: 6 }}>Membership is {m.status}{m.ended_at ? ` since ${day(m.ended_at)}` : ''}.</div>
      )}
      {email && <div style={{ fontSize: 10, color: V.text3, fontFamily: V.mono, marginTop: 6 }}>{email}{m.display_name ? ` · shown to the gym as “${m.display_name}”` : ''}</div>}
      {m.tags?.length > 0 && <div style={{ fontSize: 10, color: V.text3, marginTop: 2 }}>Tags: {m.tags.join(', ')}</div>}

      <Block title="Coaches" testId="org-context-coaches">
        {org.coaches?.length ? org.coaches.map(c => (
          <Row key={c.email} k={c.name || c.email} mono
            v={c.target_sessions_per_week != null ? `target ${c.target_sessions_per_week}/wk` : 'no target set'} />
        )) : <div style={{ fontSize: 12, color: V.text3 }}>No coach assigned.</div>}
      </Block>

      <Block title="Consent" testId="org-context-consent">
        {consentRows(org.consent).map(r => (
          <Row key={r.key} k={r.label} v={r.shared ? 'shared' : 'not shared'} color={r.shared ? V.accent : V.text3} testId={`org-consent-${r.key}`} />
        ))}
        <div style={{ fontSize: 10, color: V.text3, marginTop: 4 }}>
          {org.consent?.granted_at ? `First granted ${day(org.consent.granted_at)}` : 'Never granted'}
          {org.consent?.updated_at ? ` · updated ${day(org.consent.updated_at)}` : ''}
          {org.consent?.version ? ` · v${org.consent.version}` : ''}
        </div>
      </Block>

      {events.length > 0 && (
        <Block title={`Last consent changes (${events.length})`} testId="org-context-consent-events">
          {events.map((e, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 11, paddingTop: 2, paddingBottom: 2 }}>
              <span style={{ color: e.granted ? V.accent : V.danger, fontWeight: 700, flexShrink: 0 }}>{e.granted ? 'granted' : 'revoked'}</span>
              <span style={{ color: V.text2, flex: 1, minWidth: 0 }}>{String(e.scope || '').replace(/_/g, ' ')}{e.source ? ` · ${e.source}` : ''}</span>
              <span style={{ color: V.text3, fontFamily: V.mono, flexShrink: 0 }}>{day(e.created_at)}</span>
            </div>
          ))}
        </Block>
      )}

      <Block title="Programme & goals" testId="org-context-program">
        <Row k="Active programme" v={org.program?.name || 'None assigned'} color={org.program ? V.text : V.text3} testId="org-context-programme" />
        {org.program && <Row k="Started" v={day(org.program.start_date)} mono />}
        {org.program?.coach_email && <Row k="Assigned by" v={org.program.coach_email} mono />}
        {goals.length === 0
          ? <Row k="Active goals" v="None" color={V.text3} />
          : goals.map(g => (
            <Row key={g.id} k={g.title || g.kind}
              v={`${g.current ?? '—'} / ${g.target ?? '—'}${g.unit ? ` ${g.unit}` : ''}${g.due ? ` · due ${day(g.due)}` : ''}`} />
          ))}
      </Block>

      <Block title="Adherence (28 days)" testId="org-context-adherence">
        {adh ? (<>
          <Row k="Status" v={adherenceLabel(adh.status_bucket)} testId="org-context-bucket"
            color={adh.status_bucket === 'on_track' ? V.accent : adh.status_bucket === 'inactive' ? V.danger : V.warn} />
          <Row k="Adherence" v={pctText(adh.adherence_pct_28d)} mono testId="org-context-adherence-pct" />
          <Row k="Sessions" v={`${num(adh.sessions_28d)} in 28d · ${num(adh.sessions_this_week)} this week${adh.target ? ` of ${adh.target}` : ''}`} mono />
          <Row k="Last workout" v={adh.last_workout ? `${day(adh.last_workout)} (${num(adh.days_since_last_workout)}d ago)` : 'Never'} mono />
        </>) : <div style={{ fontSize: 12, color: V.text3 }}>No adherence row — staff accounts and members with no coach are not measured.</div>}
      </Block>

      <Block title={`Effective features · ${off.length} off of ${keys.length}`} testId="org-context-flags">
        {org.flags?.degraded && (
          <div style={{ fontSize: 10, color: V.warn, marginBottom: 4 }}>Flag lookup failed on the API — it fails open, so these are every feature ON, not the real answer.</div>
        )}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {keys.map(k => {
            const on = flags[k] !== false;
            return (
              <span key={k} data-testid={`org-context-flag-${k}`} data-on={String(on)}
                style={{ fontSize: 10, fontFamily: V.mono, color: on ? V.text2 : V.danger, background: on ? V.card : `${V.danger}14`, borderRadius: 6, paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2 }}>{k}</span>
            );
          })}
        </div>
      </Block>
    </Card>
    </div>
  );
}

// Severity colours for the health / dashboard lists, in one place so ok / warn /
// error read the same everywhere. `V` is read at call time, never at module scope.
export const severityColor = (sev) => (sev === 'error' ? V.danger : sev === 'warn' ? V.warn : V.accent);
